import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts';
import {
  LoginPage,
  DashboardPage,
  ModerationPage,
  ContentsPage,
  UsersPage,
  StatisticsPage,
  CommentsPage,
  LikesPage,
} from './components/pages';
import { DashboardLayout } from './components/templates';
import { RoleBasedProtectedRoute } from './components/ProtectedRoute';

// Component to handle default route redirection based on user role
// This only handles root path (/) and undefined routes (*)
const DefaultRoute = () => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on user role
  if (user.isSuperuser) {
    return <Navigate to="/dashboard" replace />;
  }

  if (user.isModerator || user.isStaff) {
    return <Navigate to="/moderation" replace />;
  }

  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          {/* Superuser only - Dashboard */}
          <Route
            path="/dashboard"
            element={
              <DashboardLayout>
                <RoleBasedProtectedRoute allowedRoles={['superuser']}>
                  <DashboardPage />
                </RoleBasedProtectedRoute>
              </DashboardLayout>
            }
          />

          {/* Moderator or Staff - Moderation */}
          <Route
            path="/moderation"
            element={
              <DashboardLayout>
                <RoleBasedProtectedRoute allowedRoles={['moderator', 'staff']}>
                  <ModerationPage />
                </RoleBasedProtectedRoute>
              </DashboardLayout>
            }
          />

          {/* Superuser or Moderator - Contents */}
          <Route
            path="/contents"
            element={
              <DashboardLayout>
                <RoleBasedProtectedRoute allowedRoles={['superuser', 'moderator']}>
                  <ContentsPage />
                </RoleBasedProtectedRoute>
              </DashboardLayout>
            }
          />

          {/* Staff or Superuser - Users */}
          <Route
            path="/users"
            element={
              <DashboardLayout>
                <RoleBasedProtectedRoute allowedRoles={['staff', 'superuser']}>
                  <UsersPage />
                </RoleBasedProtectedRoute>
              </DashboardLayout>
            }
          />

          {/* Superuser only - Statistics */}
          <Route
            path="/statistics"
            element={
              <DashboardLayout>
                <RoleBasedProtectedRoute allowedRoles={['superuser']}>
                  <StatisticsPage />
                </RoleBasedProtectedRoute>
              </DashboardLayout>
            }
          />

          {/* Superuser or Moderator - Likes */}
          <Route
            path="/likes"
            element={
              <DashboardLayout>
                <RoleBasedProtectedRoute allowedRoles={['superuser', 'moderator']}>
                  <LikesPage />
                </RoleBasedProtectedRoute>
              </DashboardLayout>
            }
          />

          {/* Superuser or Moderator - Comments */}
          <Route
            path="/comments"
            element={
              <DashboardLayout>
                <RoleBasedProtectedRoute allowedRoles={['superuser', 'moderator']}>
                  <CommentsPage />
                </RoleBasedProtectedRoute>
              </DashboardLayout>
            }
          />

          {/* Default route */}
          <Route path="/" element={<DefaultRoute />} />
          <Route path="*" element={<DefaultRoute />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
